import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import moment from "moment";
import "moment/locale/gu";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Filter,
  ListFilter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  Eye,
  IndianRupee,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPartiesListData,
  getParties,
  getTotalParties,
  getPartiesFilter,
  setPartiesFilter,
  getPartiesLoading,
  setEditParty,
  togglePartyModal,
} from "../../features/parties/partiesSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import { route } from "../../static/route";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PartiesProps, PartiesFilterType, IFormInput } from "../../types/parties";
import PartyFormModal from "./PartyFormModal";
import { getTypeChips } from "./typeBadge";

const typeOptions = [
  { value: "", label: t.parties.allTypes },
  { value: "purchase", label: t.parties.types.purchase },
  { value: "cutting", label: (t.parties.types as any).cutting },
  { value: "turning", label: (t.parties.types as any).turning },
  { value: "buff", label: t.parties.types.buff },
  { value: "color_process", label: t.parties.types.colorProcess },
];

const sortByOptions = [
  { value: "id", label: t.parties.sortOptions.id },
  { value: "firm_name", label: t.parties.sortOptions.firm_name },
  { value: "name", label: t.parties.sortOptions.name },
  { value: "type", label: t.parties.sortOptions.type },
  { value: "created_at", label: t.parties.sortOptions.created_at },
];

const sortOrderOptions = [
  { value: "asc", label: t.parties.sortOrderOptions.asc },
  { value: "desc", label: t.parties.sortOrderOptions.desc },
];

const PartyList = () => {
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();

  moment.locale("gu");

  const parties = useAppSelector(getParties);
  const totalParties = useAppSelector(getTotalParties);
  const filter = useAppSelector(getPartiesFilter);
  const loading = useAppSelector(getPartiesLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number>(0);

  const { register, control, handleSubmit, reset } =
    useForm<PartiesFilterType>({
      defaultValues: { ...filter },
    });

  const fetchParties = () => {
    dispatch(
      fetchPartiesListData({
        page,
        size: pageSize,
        search: filter.search,
        type: filter.type,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  const prevBranchIdRef = useRef<number | null>(null);
  const selectedBranchId = selectedBranch?.id ?? null;

  useEffect(() => {
    if (!selectedBranchId) return;

    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = selectedBranchId;

    const isBranchChange =
      prevBranchId !== null && prevBranchId !== selectedBranchId;
    if (isBranchChange && page !== 1) {
      setPage(1);
      return;
    }

    fetchParties();
  }, [page, pageSize, filter, selectedBranchId]);

  const onApplyFilters = (data: PartiesFilterType) => {
    dispatch(
      setPartiesFilter({
        search: data.search || null,
        type: data.type || null,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      })
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults: PartiesFilterType = {
      search: null,
      type: null,
      sortBy: "id",
      sortOrder: "desc",
    };
    reset(defaults);
    dispatch(setPartiesFilter(defaults));
    setPage(1);
  };

  const handleShowPartyForm = async () => {
    await dispatch(setEditParty(null));
    dispatch(togglePartyModal(true));
  };

  const handleSaveParty = async (data: IFormInput) => {
    try {
      const storeEditParty = (await import("../../app/store")).store.getState()
        .parties.editParty;

      if (storeEditParty && storeEditParty.id) {
        const response: any = await PUT(`/v1/parties/${storeEditParty.id}`, {
          firm_name: data.firm_name,
          name: data.name,
          process_definition_ids: data.process_definition_ids || [],
          phone: data.phone,
          wp_phone: data.wp_phone,
          address: data.address,
        });
        if (response.data.status === "success") {
          await dispatch(setEditParty(null));
          if (page === 1) {
            fetchParties();
          } else {
            setPage(1);
          }
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } else {
        const response: any = await POST("/v1/parties", {
          firm_name: data.firm_name,
          name: data.name,
          process_definition_ids: data.process_definition_ids || [],
          phone: data.phone,
          wp_phone: data.wp_phone,
          address: data.address,
        });
        if (response.data.status === "success") {
          if (page === 1) {
            fetchParties();
          } else {
            setPage(1);
          }
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  const handleDeleteParty = async (id: number) => {
    const confirmed = await confirmDelete(
      t.parties.deleteConfirmText,
      t.parties.deleteConfirmTitle,
      t.parties.deleteConfirmButton
    );

    if (confirmed) {
      setActionLoadingId(id);
      try {
        const response: any = await DELETE(`/v1/parties/${id}`);
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });
          const newPage = parties.length > 1 ? page : Math.max(page - 1, 1);
          if (newPage === page) {
            fetchParties();
          } else {
            setPage(newPage);
          }
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || t.parties.inUseError,
          { ...(toastStyles as any) }
        );
      }
      setActionLoadingId(0);
    }
  };

  const handleEditParty = async (party: PartiesProps) => {
    await dispatch(setEditParty(party));
    dispatch(togglePartyModal(true));
  };

  const totalPages = Math.ceil(totalParties / pageSize);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalParties === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalParties);

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t.parties.filterToggle}
          </Button>

          {canAccess("parties.create") && (
            <Button
              className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              onClick={handleShowPartyForm}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.parties.addNewParty}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.parties.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.parties.searchLabel}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                      placeholder={t.parties.searchPlaceholder}
                      {...register("search")}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.parties.typeLabel}
                  </label>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        styles={filterSelectStyles}
                        options={typeOptions}
                        placeholder={t.parties.typePlaceholder}
                        value={typeOptions.find(
                          (o) => o.value === (field.value || "")
                        )}
                        onChange={(opt: any) =>
                          field.onChange(opt?.value || null)
                        }
                      />
                    )}
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.parties.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          placeholder={t.parties.sortByPlaceholder}
                          value={sortByOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "id")
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.parties.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          placeholder={t.parties.sortOrderPlaceholder}
                          value={sortOrderOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "desc")
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
                  onClick={handleClearFilters}
                >
                  {t.parties.clearFilters}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
                >
                  {t.parties.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && parties.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.parties.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && parties.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.parties.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div
            className={`space-y-3${
              loading ? " opacity-50 pointer-events-none" : ""
            }`}
          >
            {parties.map((party: PartiesProps, index: number) => (
              <Card key={party.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-gray-900">
                        {party.firm_name || "-"}
                      </span>
                      <div className="text-sm text-gray-500">
                        {party.name || "-"}
                      </div>
                      {party.process_definitions && party.process_definitions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {getTypeChips(party.process_definitions).map((chip, i) => (
                            <Badge key={i} className={`text-xs ${chip.className}`}>
                              {chip.label}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      {actionLoadingId === party.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67]" />
                      ) : (
                        <div className="flex items-center gap-1">
                          <Link
                            to={route.partiesView.replace(":id", String(party.id))}
                          >
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10">
                              <Eye className="h-4 w-4 text-[#113F67]" />
                            </Button>
                          </Link>
                          <Link
                            to={route.payments.replace(
                              ":id",
                              String(party.id)
                            )}
                          >
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10">
                              <CreditCard className="h-4 w-4 text-[#113F67]" />
                            </Button>
                          </Link>
                          {canAccess("parties.rate") && (
                            <Link
                              to={route.partiesRates.replace(":id", String(party.id))}
                            >
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10">
                                <IndianRupee className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            </Link>
                          )}
                          {canAccess("parties.update") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                              onClick={() => handleEditParty(party)}
                            >
                              <Pencil className="h-4 w-4 text-[#113F67]" />
                            </Button>
                          )}
                          {canAccess("parties.delete") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg hover:bg-red-50"
                              onClick={() => handleDeleteParty(party.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.parties.table.phone}</span>
                      <span className="text-gray-700">{party.phone || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.parties.table.date}</span>
                      <span className="text-gray-700">
                        {moment(party.created_at).format("DD MMMM, YYYY hh:mm A")}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">{t.parties.table.srNo}</TableHead>
                  <TableHead>{t.parties.table.firmName}</TableHead>
                  <TableHead>{t.parties.table.partyName}</TableHead>
                  <TableHead>{t.parties.table.type}</TableHead>
                  <TableHead>{t.parties.table.phone}</TableHead>
                  <TableHead>{t.parties.table.date}</TableHead>
                  <TableHead className="text-right">{t.parties.table.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.parties.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : parties.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-gray-500"
                    >
                      {t.parties.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  parties.map((party: PartiesProps, index: number) => (
                    <TableRow className="align-top" key={party.id}>
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-[#113F67]">
                          {party.firm_name || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600">{party.name || "-"}</TableCell>
                      <TableCell>
                        {party.process_definitions && party.process_definitions.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {getTypeChips(party.process_definitions).map((chip, i) => (
                              <Badge key={i} className={`text-xs ${chip.className}`}>
                                {chip.label}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <div>{party.phone || "-"}</div>
                        {party.wp_phone && (
                          <div className="text-xs text-gray-400">
                            WP: {party.wp_phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {moment(party.created_at).format(
                          "DD MMMM, YYYY hh:mm A"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoadingId === party.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67] ml-auto" />
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={route.partiesView.replace(":id", String(party.id))}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                                title={(t.parties as any).viewAction || "વિગતો"}
                              >
                                <Eye className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            </Link>
                            <Link
                              to={route.payments.replace(
                                ":id",
                                String(party.id)
                              )}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                                title={t.parties.paymentAction}
                              >
                                <CreditCard className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            </Link>
                            {canAccess("parties.rate") && (
                              <Link
                                to={route.partiesRates.replace(":id", String(party.id))}
                              >
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                                  title={(t.parties as any).rateAction || "દર"}
                                >
                                  <IndianRupee className="h-4 w-4 text-[#113F67]" />
                                </Button>
                              </Link>
                            )}
                            {canAccess("parties.update") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                                title={t.parties.editAction}
                                onClick={() => handleEditParty(party)}
                              >
                                <Pencil className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            )}
                            {canAccess("parties.delete") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg hover:bg-red-50"
                                title={t.parties.deleteAction}
                                onClick={() => handleDeleteParty(party.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && parties.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalParties} {t.parties.pageOfLabel}
            </span>
            <span>{t.parties.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${
                    page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""
                  }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Party Form Modal */}
      <PartyFormModal handleSaveParty={handleSaveParty} />
    </div>
  );
};

export default PartyList;
